/**
 * Story Media Manager
 * Global coordinator for all media modules (Audio, Video, 360°, etc.)
 *
 * Purpose:
 * - Single-Medium Enforcement: Only ONE medium plays at any time
 * - Event-based Coordination: React to Panel activation/deactivation
 * - Module Registry: All media modules register here
 * - User Gesture Gate: Autoplay policy compliance
 * - Mobile-First: Touch support, performance optimization
 *
 * Module Contract:
 * Every media module MUST implement:
 * - name: string (unique identifier)
 * - type: 'audio' | 'video' | 'panorama'
 * - init(panel): Initialize when panel activated
 * - start(element): User interaction → play media
 * - pause(): Pause media (keep state)
 * - stop(): Stop media (reset state)
 * - cleanup(): Free resources (optional)
 * - isPlaying: boolean
 * - panel: HTMLElement | null
 * - element: HTMLElement | null (current media element)
 *
 * Usage:
 * // In module
 * window.StoryMediaManager.register(this);
 *
 * // When user interacts (click/hover)
 * window.StoryMediaManager.requestPlay(this);
 *
 * Dependencies: Active Panel System (story:panel:activated/deactivated events)
 * File: assets/js/story-media-manager.js
 */

(function(window) {
  'use strict';

  /* ----------------------------------------------------------
     1. Media Manager Class
     ---------------------------------------------------------- */

  class StoryMediaManager {
    constructor() {
      // Module Registry
      this.modules = [];

      // Global State
      this.currentActiveMedia = null;  // Currently playing module
      this.activePanel = null;         // Current active panel
      this.userGestureReceived = false; // Autoplay allowed?

      // Mobile Detection
      this.isMobile = this.detectMobile();

      // Initialize
      this.init();
    }

    /* ----------------------------------------------------------
       Initialization
       ---------------------------------------------------------- */

    init() {
      // Listen to Panel Events
      window.addEventListener('story:panel:activated', this.handlePanelActivated.bind(this));
      window.addEventListener('story:panel:deactivated', this.handlePanelDeactivated.bind(this));

      // User Gesture Detection (for Autoplay Policy)
      document.addEventListener('click', this.enableAutoplay.bind(this), { once: true });
      document.addEventListener('touchstart', this.enableAutoplay.bind(this), { once: true });

      // Log initialization (development only)
      if (window.location.search.includes('debug=story')) {
        console.log('[MediaManager] Initialized - Mobile:', this.isMobile);
      }
    }

    /* ----------------------------------------------------------
       Mobile Detection
       ---------------------------------------------------------- */

    detectMobile() {
      return (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        navigator.msMaxTouchPoints > 0
      );
    }

    /* ----------------------------------------------------------
       User Gesture Gate
       ---------------------------------------------------------- */

    enableAutoplay() {
      this.userGestureReceived = true;
      if (this._audioUnlocked) return; // Already unlocked
      this._audioUnlocked = true;

      // Unlock Web Audio API (Safari requires AudioContext.resume during user gesture)
      try {
        var AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx && !this._audioCtx) {
          this._audioCtx = new AudioCtx();
          var buf = this._audioCtx.createBuffer(1, 1, 22050);
          var src = this._audioCtx.createBufferSource();
          src.buffer = buf;
          src.connect(this._audioCtx.destination);
          src.start(0);
          this._audioCtx.resume();
        }
      } catch (e) { /* silent fail */ }

      // Unlock HTML5 Audio (iOS Safari needs a play() call during user gesture)
      try {
        var a = document.createElement('audio');
        a.setAttribute('src', 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=');
        a.play().then(function() { a.remove(); }).catch(function() {});
      } catch (e) { /* silent fail */ }

      // Unlock audio on all registered modules (mobile Safari requires
      // play() on the actual Audio elements during user gesture)
      this.modules.forEach(function(mod) {
        if (typeof mod.unlockAudio === 'function') {
          try { mod.unlockAudio(); } catch (e) { /* silent */ }
        }
      });

      if (window.location.search.includes('debug=story')) {
        console.log('[MediaManager] User gesture received - Audio unlocked');
      }
    }

    /* ----------------------------------------------------------
       Module Registration
       ---------------------------------------------------------- */

    register(module) {
      // Validate module contract
      if (!this.validateModule(module)) {
        console.warn('[MediaManager] Invalid module contract:', module);
        return false;
      }

      // Check for duplicate name
      const exists = this.modules.find(m => m.name === module.name);
      if (exists) {
        console.warn('[MediaManager] Module already registered:', module.name);
        return false;
      }

      // Register module
      this.modules.push(module);

      if (window.location.search.includes('debug=story')) {
        console.log('[MediaManager] Module registered:', module.name, '(type:', module.type + ')');
      }

      return true;
    }

    unregister(module) {
      const index = this.modules.findIndex(m => m.name === module.name);
      if (index !== -1) {
        this.modules.splice(index, 1);

        if (window.location.search.includes('debug=story')) {
          console.log('[MediaManager] Module unregistered:', module.name);
        }

        return true;
      }
      return false;
    }

    validateModule(module) {
      // Check required properties
      if (!module.name || typeof module.name !== 'string') return false;
      if (!module.type || typeof module.type !== 'string') return false;

      // Check required methods
      if (typeof module.init !== 'function') return false;
      if (typeof module.start !== 'function') return false;
      if (typeof module.pause !== 'function') return false;
      if (typeof module.stop !== 'function') return false;

      // cleanup() is optional
      // if (typeof module.cleanup !== 'function') return false;

      return true;
    }

    /* ----------------------------------------------------------
       Panel Event Handlers
       ---------------------------------------------------------- */

    handlePanelActivated(e) {
      const panel = e.detail.panel;
      const index = e.detail.index;

      this.activePanel = panel;

      if (window.location.search.includes('debug=story')) {
        console.log('[MediaManager] Panel activated:', index);
      }

      // Initialize all modules in this panel
      this.modules.forEach(module => {
        try {
          module.init(panel);
        } catch (error) {
          console.error('[MediaManager] Error initializing module:', module.name, error);
        }
      });

      // Note: We do NOT auto-start media here
      // Media only starts on user interaction (click/hover)
    }

    handlePanelDeactivated(e) {
      const panel = e.detail.panel;
      const index = e.detail.index;

      if (window.location.search.includes('debug=story')) {
        console.log('[MediaManager] Panel deactivated:', index);
      }

      // Stop all media in the deactivated panel
      this.stopAllMediaInPanel(panel);

      // Clear active panel if it matches
      if (this.activePanel === panel) {
        this.activePanel = null;
      }
    }

    /* ----------------------------------------------------------
       Media Control
       ---------------------------------------------------------- */

    requestPlay(module) {
      // Validate module is registered
      if (!this.modules.includes(module)) {
        console.warn('[MediaManager] Module not registered:', module.name);
        return false;
      }

      // Single-Medium Enforcement
      if (this.currentActiveMedia && this.currentActiveMedia !== module) {
        if (window.location.search.includes('debug=story')) {
          console.log('[MediaManager] Stopping current media:', this.currentActiveMedia.name);
        }

        // Pause previous media
        try {
          this.currentActiveMedia.pause();
        } catch (error) {
          console.error('[MediaManager] Error pausing media:', this.currentActiveMedia.name, error);
        }
      }

      // Set new active media
      this.currentActiveMedia = module;

      // Notify ambient audio to duck
      window.dispatchEvent(new Event('story:media:ducking'));

      if (window.location.search.includes('debug=story')) {
        console.log('[MediaManager] Now playing:', module.name, '(type:', module.type + ')');
      }

      return true;
    }

    notifyStop(module) {
      // Module reports it has stopped
      if (this.currentActiveMedia === module) {
        this.currentActiveMedia = null;

        // Notify ambient audio to restore volume
        window.dispatchEvent(new Event('story:media:unducking'));

        if (window.location.search.includes('debug=story')) {
          console.log('[MediaManager] Media stopped:', module.name);
        }
      }
    }

    /* ----------------------------------------------------------
       Globaler Mute (AP-B/B3, 2026-07-23)
       EIN Ton-Knopf muss ALLES erreichen: O-Ton, Bubbles, Galerie-
       Audio, Pano-Hotspots, Atmo. Der Ambient-Toggle ruft dies auf;
       der Manager fächert an alle registrierten Module auf, die
       setMuted anbieten. Neue Medien starten stumm, solange muted.
       ---------------------------------------------------------- */

    setMuted(muted) {
      this.muted = !!muted;

      this.modules.forEach((module) => {
        if (typeof module.setMuted === 'function') {
          try {
            module.setMuted(this.muted);
          } catch (error) {
            console.error('[MediaManager] setMuted-Fehler in', module.name, error);
          }
        }
      });

      window.dispatchEvent(new CustomEvent('story:media:muted', {
        detail: { muted: this.muted }
      }));

      if (window.location.search.includes('debug=story')) {
        console.log('[MediaManager] Global mute:', this.muted);
      }
    }

    stopAll() {
      // Stop all currently playing media (emergency stop)
      if (this.currentActiveMedia) {
        try {
          this.currentActiveMedia.stop();
        } catch (error) {
          console.error('[MediaManager] Error stopping media:', this.currentActiveMedia.name, error);
        }

        this.currentActiveMedia = null;
      }
    }

    stopAllMediaInPanel(panel) {
      // Stop all media associated with this panel
      this.modules.forEach(module => {
        if (module.panel === panel && module.isPlaying) {
          if (window.location.search.includes('debug=story')) {
            console.log('[MediaManager] Stopping module in panel:', module.name);
          }

          try {
            module.pause();

            // Optional: Call cleanup if module provides it
            if (typeof module.cleanup === 'function') {
              module.cleanup();
            }
          } catch (error) {
            console.error('[MediaManager] Error stopping module:', module.name, error);
          }
        }
      });

      // Clear currentActiveMedia if it was in this panel
      if (this.currentActiveMedia && this.currentActiveMedia.panel === panel) {
        this.currentActiveMedia = null;
      }
    }

    /* ----------------------------------------------------------
       Utility Methods
       ---------------------------------------------------------- */

    getModuleByName(name) {
      return this.modules.find(m => m.name === name);
    }

    getModulesByType(type) {
      return this.modules.filter(m => m.type === type);
    }

    getActiveModule() {
      return this.currentActiveMedia;
    }

    hasUserGesture() {
      return this.userGestureReceived;
    }

    /* ----------------------------------------------------------
       Debug Info
       ---------------------------------------------------------- */

    getDebugInfo() {
      return {
        registeredModules: this.modules.map(m => ({
          name: m.name,
          type: m.type,
          isPlaying: m.isPlaying
        })),
        currentActiveMedia: this.currentActiveMedia ? this.currentActiveMedia.name : null,
        activePanel: this.activePanel ? 'Panel-' + Array.from(document.querySelectorAll('.story-panel')).indexOf(this.activePanel) : null,
        userGestureReceived: this.userGestureReceived,
        isMobile: this.isMobile
      };
    }
  }

  /* ----------------------------------------------------------
     2. Initialize Global Instance
     ---------------------------------------------------------- */

  // Create single global instance
  if (!window.StoryMediaManager) {
    window.StoryMediaManager = new StoryMediaManager();
  }

  // Expose for debugging
  if (window.location.search.includes('debug=story')) {
    window.addEventListener('DOMContentLoaded', function() {
      console.log('[MediaManager] Debug mode enabled. Use StoryMediaManager.getDebugInfo() for info.');
    });
  }

})(window);
